import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { label: "", username: "", password: "", url: "", notes: "" };

export default function LeadPassesPanel({ leadId }) {
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reveal, setReveal] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/leads/${leadId}/passes`);
      setPasses(data.passes || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const startAdd = () => {
    setDraft(EMPTY);
    setEditId(null);
    setAdding(true);
  };
  const startEdit = (p) => {
    setDraft({
      label: p.label || "",
      username: p.username || "",
      password: p.password || "",
      url: p.url || "",
      notes: p.notes || "",
    });
    setEditId(p.id);
    setAdding(true);
  };

  const save = async () => {
    if (!draft.label.trim()) {
      toast.error("Add a label (e.g. University Portal)");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/admin/leads/${leadId}/passes/${editId}`, draft);
      } else {
        await api.post(`/admin/leads/${leadId}/passes`, draft);
      }
      toast.success("Saved");
      setAdding(false);
      setEditId(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/admin/leads/${leadId}/passes/${p.id}`);
      setPasses((x) => x.filter((i) => i.id !== p.id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copy = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`));
  };

  return (
    <div data-testid="lead-passes-panel">
      <div className="mb-2 flex items-center justify-between">
        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
          <KeyRound className="h-3.5 w-3.5" /> Passes
        </label>
        {!adding && (
          <Button size="sm" variant="ghost" onClick={startAdd} className="h-7 text-[#1877F2] hover:bg-blue-50" data-testid="pass-add-btn">
            <Plus className="mr-1 h-3.5 w-3.5" /> Add
          </Button>
        )}
      </div>

      {adding && (
        <div className="mb-3 space-y-2 rounded-lg border border-slate-200 bg-slate-50/50 p-3" data-testid="pass-form">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              {editId ? "Edit pass" : "New pass"}
            </span>
            <button type="button" onClick={() => { setAdding(false); setEditId(null); }} className="text-slate-400 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          </div>
          <Input placeholder="Label (e.g. University Portal)" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} className="h-8 text-xs" data-testid="pass-label" />
          <Input placeholder="Username / email" value={draft.username} onChange={(e) => setDraft({ ...draft, username: e.target.value })} className="h-8 text-xs" data-testid="pass-username" />
          <Input placeholder="Password" value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} className="h-8 text-xs" data-testid="pass-password" />
          <Input placeholder="URL (optional)" value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} className="h-8 text-xs" data-testid="pass-url" />
          <Input placeholder="Notes (optional)" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} className="h-8 text-xs" data-testid="pass-notes" />
          <Button size="sm" onClick={save} disabled={saving} className="w-full bg-[#0A2540] hover:bg-[#1E3A5F]" data-testid="pass-save">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            {editId ? "Update" : "Save pass"}
          </Button>
        </div>
      )}

      <div className="space-y-1.5">
        {loading ? (
          <div className="py-3 text-center text-xs text-slate-400">
            <Loader2 className="mr-1.5 inline h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : passes.length === 0 && !adding ? (
          <div className="rounded-lg border border-dashed border-slate-200 py-4 text-center text-xs text-slate-400">
            No saved logins yet.
          </div>
        ) : (
          passes.map((p) => (
            <div key={p.id} className="rounded-lg border border-slate-100 bg-slate-50/50 px-2.5 py-2" data-testid={`pass-row-${p.id}`}>
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-medium text-[#0A2540]">{p.label}</div>
                  {p.username && <div className="truncate text-[11px] text-slate-500">{p.username}</div>}
                </div>
                <button type="button" onClick={() => startEdit(p)} className="rounded-md p-1.5 text-slate-400 hover:bg-white hover:text-[#1877F2]" data-testid={`pass-edit-${p.id}`} aria-label="Edit">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={() => remove(p)} className="rounded-md p-1.5 text-rose-400 hover:bg-rose-50" data-testid={`pass-delete-${p.id}`} aria-label="Delete">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              {p.password && (
                <div className="mt-1.5 flex items-center gap-1.5">
                  <code className="flex-1 truncate rounded bg-white px-2 py-1 font-mono text-[11px] text-slate-700" data-testid={`pass-value-${p.id}`}>
                    {reveal[p.id] ? p.password : "••••••••"}
                  </code>
                  <button type="button" onClick={() => setReveal((r) => ({ ...r, [p.id]: !r[p.id] }))} className="rounded-md p-1.5 text-slate-400 hover:text-slate-600" data-testid={`pass-reveal-${p.id}`} aria-label="Reveal">
                    {reveal[p.id] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                  <button type="button" onClick={() => copy(p.password, "Password")} className="rounded-md p-1.5 text-slate-400 hover:text-[#1877F2]" data-testid={`pass-copy-${p.id}`} aria-label="Copy">
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              {p.url && (
                <a href={p.url.startsWith("http") ? p.url : `https://${p.url}`} target="_blank" rel="noreferrer" className="mt-1 block truncate text-[11px] text-[#1877F2] hover:underline">
                  {p.url}
                </a>
              )}
              {p.notes && <div className="mt-1 text-[11px] text-slate-400">{p.notes}</div>}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
